import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import useSubscriptionStatus from "../lib/useSubscriptionStatus";
import { toast } from "sonner";
import { LogOut, Crown, Mail, Calendar, Flame, Sparkles, Gift, BookHeart, Bookmark, MessageSquare, ChevronRight, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import PaywallModal from "../components/PaywallModal";
import GiftModal from "../components/GiftModal";
import TrialBanner from "../components/TrialBanner";
import ChangePasswordPanel from "../components/ChangePasswordPanel";
import SmsPreferencesPanel from "../components/SmsPreferencesPanel";

export default function ProfilePage() {
  const { user, logout } = useAuth();
  const {
    plan,
    isTrialActive,
    isTrialExpired,
    isGift,
    isGiftActive,
    isPaidSubscriber,
    isPaid,
    expiresAt,
  } = useSubscriptionStatus();
  const [paywall, setPaywall] = useState(false);
  const [giftOpen, setGiftOpen] = useState(false);

  if (!user) return null;

  return (
    <div className="space-y-6">
      <div className="fade-in-up">
        <h1 className="font-display text-3xl text-[#2E2E2E]">Profile</h1>
      </div>

      <div data-testid="profile-card" className="rounded-3xl border border-[#DCE5DB] bg-white p-7 shadow-[0_4px_12px_rgba(0,0,0,0.04)] fade-in-up stagger-1">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-[#4F6F52] text-white flex items-center justify-center font-display text-2xl shadow-[0_4px_14px_rgba(79,111,82,0.25)]">
            {(user.name || user.email)[0].toUpperCase()}
          </div>
          <div>
            <div className="font-display text-xl text-[#2E2E2E]">{user.name || "Friend"}</div>
            <div className="text-sm text-[#6B6B6B] flex items-center gap-1.5 mt-0.5">
              <Mail size={12} /> {user.email}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 mt-6">
          <div className="rounded-2xl bg-[#EEF3EC] px-4 py-3.5 border border-[#DCE5DB]">
            <div className="text-xs text-[#6B6B6B] flex items-center gap-1.5"><Flame size={11} /> Showing up</div>
            <div className="font-display text-2xl text-[#4F6F52] mt-0.5">{user.streak || 0}</div>
          </div>
          <div className="rounded-2xl bg-[#EEF3EC] px-4 py-3.5 border border-[#DCE5DB]">
            <div className="text-xs text-[#6B6B6B] flex items-center gap-1.5"><Calendar size={11} /> Joined</div>
            <div className="font-medium text-[#2E2E2E] mt-1">{new Date(user.created_at).toLocaleDateString()}</div>
          </div>
        </div>
      </div>

      {/* Active trial → colored banner. Only renders while the trial is truly ACTIVE. */}
      {isTrialActive && <TrialBanner compact={false} />}

      {/* Expired trial → distinct card with exact copy, no misleading "full Pro" wording. */}
      {isTrialExpired && (
        <div
          data-testid="expired-trial-card"
          className="rounded-3xl border border-[#DCE5DB] bg-white p-7 shadow-[0_4px_12px_rgba(0,0,0,0.04)] fade-in-up stagger-2"
        >
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={16} className="text-[#8C7A4F]" />
            <span className="text-xs uppercase tracking-[0.16em] font-semibold text-[#8C7A4F]">
              Pro trial ended
            </span>
          </div>
          <p
            data-testid="expired-trial-headline"
            className="font-display text-xl text-[#2E2E2E] mb-1.5"
          >
            Your LifeGuide Pro trial has ended.
          </p>
          <p className="text-[15px] text-[#4E5A4C] leading-relaxed mb-5">
            You can continue using LifeGuide on the free plan, or upgrade to Pro for unlimited access.
          </p>
          <button
            data-testid="expired-trial-upgrade"
            onClick={() => setPaywall(true)}
            className="bg-[#4F6F52] hover:bg-[#3F5A42] text-white rounded-full px-6 py-3 text-sm font-semibold transition shadow-[0_4px_14px_rgba(79,111,82,0.18)] inline-flex items-center gap-2"
          >
            Continue with LifeGuide Pro — $3.99/month <ArrowRight size={14} strokeWidth={2.4} />
          </button>
        </div>
      )}

      {/* Paid / Gift / plain-free card — hidden during the trial states above. */}
      {!isTrialActive && !isTrialExpired && (
      <div
        data-testid="plan-card"
        className={`rounded-3xl border p-7 shadow-[0_4px_12px_rgba(0,0,0,0.04)] fade-in-up stagger-2 ${
          isGiftActive
            ? "bg-[#F5EBD9] border-[#D6C7A1]/60"
            : isPaidSubscriber
            ? "bg-[#E1ECDD] border-[#4F6F52]/30"
            : "bg-white border-[#DCE5DB]"
        }`}
      >
        <div className="flex items-center gap-2 mb-3">
          {isGiftActive ? (
            <Crown size={16} className="text-[#8C7A4F]" />
          ) : isPaidSubscriber ? (
            <Sparkles size={16} className="text-[#4F6F52]" />
          ) : (
            <Sparkles size={16} className="text-[#6B6B6B]" />
          )}
          <span className={`text-xs uppercase tracking-[0.16em] font-semibold ${
            isGiftActive ? "text-[#8C7A4F]" : isPaidSubscriber ? "text-[#4F6F52]" : "text-[#6B6B6B]"
          }`}>
            {isGiftActive ? "Gift active" : isPaidSubscriber ? "Pro plan" : "Free plan"}
          </span>
        </div>
        {isPaid ? (
          <>
            <p className="text-[15px] text-[#2E2E2E] leading-relaxed mb-2">
              {isGiftActive
                ? "Full Pro access from your gift — unlimited guidance, coach chat, and message analyzer."
                : "Unlimited guidance, unlimited coaching, and the full message analyzer."}
            </p>
            {expiresAt && (
              <p className="text-xs text-[#6B6B6B]">
                {isGiftActive ? "Access until " : "Renews on "}
                {expiresAt.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}.
              </p>
            )}
            {isGiftActive && (
              <button
                data-testid="upgrade-after-gift"
                onClick={() => setPaywall(true)}
                className="mt-4 bg-[#4F6F52] hover:bg-[#3F5A42] text-white rounded-full px-5 py-2.5 text-sm font-medium transition shadow-[0_4px_14px_rgba(79,111,82,0.18)]"
              >
                Continue with LifeGuide Pro — $3.99/month
              </button>
            )}
          </>
        ) : (
          <>
            <p className="text-[15px] text-[#2E2E2E] mb-4 leading-relaxed">
              You're on the free plan — 1 guidance moment and 1 coach reply per day.
              Upgrade for unlimited access whenever you need it most.
            </p>
            <button
              data-testid="upgrade-btn"
              onClick={() => setPaywall(true)}
              className="bg-[#4F6F52] hover:bg-[#3F5A42] text-white rounded-full px-6 py-3 text-sm font-medium transition shadow-[0_4px_14px_rgba(79,111,82,0.18)]"
            >
              See plans
            </button>
          </>
        )}
      </div>
      )}

      <ChangePasswordPanel />

      <SmsPreferencesPanel />


      <Link
        to="/journal"
        data-testid="profile-journal-link"
        className="w-full bg-white border border-[#DCE5DB] hover:border-[#4F6F52]/40 hover:bg-[#EEF3EC]/60 text-[#2E2E2E] rounded-2xl py-3.5 px-5 flex items-center justify-between transition shadow-[0_4px_12px_rgba(0,0,0,0.03)]"
      >
        <span className="flex items-center gap-2">
          <BookHeart size={16} className="text-[#4F6F52]" />
          <span>My Journal</span>
        </span>
        <ChevronRight size={16} className="text-[#6B6B6B]" />
      </Link>

      <Link
        to="/saved"
        data-testid="profile-saved-wisdom-link"
        className="w-full bg-white border border-[#DCE5DB] hover:border-[#4F6F52]/40 hover:bg-[#EEF3EC]/60 text-[#2E2E2E] rounded-2xl py-3.5 px-5 flex items-center justify-between transition shadow-[0_4px_12px_rgba(0,0,0,0.03)]"
      >
        <span className="flex items-center gap-2">
          <Bookmark size={16} className="text-[#4F6F52]" />
          <span>Saved Wisdom</span>
        </span>
        <ChevronRight size={16} className="text-[#6B6B6B]" />
      </Link>

      <Link
        to="/sms"
        data-testid="profile-sms-updates-link"
        className="w-full bg-white border border-[#DCE5DB] hover:border-[#4F6F52]/40 hover:bg-[#EEF3EC]/60 text-[#2E2E2E] rounded-2xl py-3.5 px-5 flex items-center justify-between transition shadow-[0_4px_12px_rgba(0,0,0,0.03)]"
      >
        <span className="flex items-center gap-2">
          <MessageSquare size={16} className="text-[#4F6F52]" />
          <span>SMS Updates</span>
        </span>
        <ChevronRight size={16} className="text-[#6B6B6B]" />
      </Link>

      <button
        data-testid="give-lifeguide-btn"
        onClick={() => setGiftOpen(true)}
        className="w-full bg-white border border-[#DCE5DB] hover:border-[#4F6F52]/40 hover:bg-[#EEF3EC]/60 text-[#2E2E2E] rounded-2xl py-3.5 inline-flex items-center justify-center gap-2 transition shadow-[0_4px_12px_rgba(0,0,0,0.03)]"
      >
        <Gift size={16} className="text-[#4F6F52]" />
        <span>Give LifeGuide to someone</span>
      </button>

      <button
        data-testid="logout-btn"
        onClick={() => {
          logout();
          toast.success("Signed out");
        }}
        className="w-full bg-white border border-[#DCE5DB] hover:border-[#A86B4A]/30 hover:bg-[#F5E6D3]/50 text-[#A86B4A] rounded-2xl py-3.5 inline-flex items-center justify-center gap-2 transition shadow-[0_4px_12px_rgba(0,0,0,0.03)]"
      >
        <LogOut size={16} /> Sign out
      </button>

      <PaywallModal open={paywall} onClose={() => setPaywall(false)} />
      <GiftModal open={giftOpen} onClose={() => setGiftOpen(false)} />
    </div>
  );
}
